import React, { useMemo, useState } from 'react';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { ClickAwayListener } from '@mui/material';
import Button from 'shared/components/andtComponents/Button';
import Input from 'shared/components/andtComponents/Input';
import styles from './CurrencySelector.module.scss';
import { CurrencySelectorOptions } from './CurrenciesSelectorOptions';
import { Currency } from 'shared/components/CurrencySelect/types';
import { filterCurrencies } from 'shared/components/CurrencySelect/Currency.utils';

interface CurrencySelectorProps {
  currencyList: Currency[];
  onClose: (event: MouseEvent | TouchEvent) => void;
  onApply: Function;
  selectedCurrency?: Currency | null;
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  onClose,
  currencyList,
  onApply,
  selectedCurrency,
}: CurrencySelectorProps): React.ReactNode => {
  const [searchText, setSearchText] = React.useState('');
  const [selectedLocalCurrency, setSelectedLocalCurrency] = useState(selectedCurrency || null);
  const [keyboardSelectedCurrency, setKeyboardSelectedCurrency] = useState<Currency | null>(null);
  const filteredCurrencies: Currency[] = useMemo(
    () => filterCurrencies(currencyList, searchText),
    [currencyList, searchText],
  );

  const handleSelectCurrency = (currency) => {
    setSelectedLocalCurrency(currency);
  };

  const handleKeyPress = (key: string) => {
    if (key === 'Enter') {
      if (selectedLocalCurrency?.code !== selectedCurrency?.code) {
        onApply(selectedLocalCurrency);
      } else if (keyboardSelectedCurrency) {
          onApply(keyboardSelectedCurrency);
        }
      
      return;
    }
    if (key === 'Escape') {
      onClose({} as any);
      return;
    }
    if (key === 'ArrowDown') {
      const index = filteredCurrencies.findIndex((currency) => currency.code === keyboardSelectedCurrency?.code);
      if (index === -1 || index + 1 >= filteredCurrencies.length) {
        setKeyboardSelectedCurrency(filteredCurrencies[0]);
      } else {
        setKeyboardSelectedCurrency(filteredCurrencies[index + 1]);
      }
      return;
    }
    if (key === 'ArrowUp') {
      const index = filteredCurrencies.findIndex((currency) => currency.code === keyboardSelectedCurrency?.code);
      if (index === -1) {
        setKeyboardSelectedCurrency(filteredCurrencies[filteredCurrencies.length - 1]);
        return;
      }
      setKeyboardSelectedCurrency(filteredCurrencies[index - 1]);
    }
  };
  return (
    <ClickAwayListener onClickAway={onClose}>
      <div className={styles.container}>
        <div className={styles.searchContainer}>
          <Input
            value={searchText}
            onKeyDown={(e) => {
              handleKeyPress(e.key);
            }}
            isAutoFocus={true}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search"
            isSearch
            searchComponent={<GenerateIcon iconName={ICONS.search.name} />}
            type="text"
            className={styles.currencySearch}
          />
        </div>
        <div className={styles.content}>
          <CurrencySelectorOptions
            searchText={searchText}
            options={filteredCurrencies}
            handleSelectCurrency={handleSelectCurrency}
            selectedCurrency={selectedLocalCurrency}
            keyboardSelectedCurrency={keyboardSelectedCurrency}
          />
        </div>
        <div className={styles.footer}>
          <Button text="Apply" onClick={() => onApply(selectedLocalCurrency)} disabled={!selectedLocalCurrency} />
        </div>
      </div>
    </ClickAwayListener>
  );
};

export default CurrencySelector;
